import ko from "knockout";
import OT from "@vonage/client-sdk-video";
import moment from "moment";
import { AppViewModel } from "./AppViewModel.js";
import { AlertViewModel } from "./AlertViewModel.js";
import { LoginViewModel } from "./LoginViewModel.js";
import { LogViewModel } from "./LogViewModel.js";
import { SupervisorCheckViewModel } from "./SupervisorCheckViewModel.js";
import { getFormattedTime, formatDuration } from "../utils/index.js";
import pino from "pino";
import AgentLegManager from "../utils/AgentLegManager.js";

// Initialize Pino logger
// Base 'logger' kept for backward compat; prefer scLogger() for scoped logs
const logger = pino({
  level: "debug",
  transport: { target: "pino-pretty", options: { colorize: true } },
});

/**
 * Manages the supervisor/interpreter session lifecycle, media streams, and UI state.
 *
 * -- Plain fields (initialized in initializeSessionState()) --
 * monitoredAgentId        : ID of the agent being monitored
 * currentMonitorAgentName : Name of the agent being monitored
 * currentRemoteStream     : OT Subscriber stream for remote audio/video
 * audioRemoteElementRef   : <audio> element reference for remote audio output
 * monitorApi              : IncontactAPI instance for interpreter (monitor) session
 * supervisorApi           : IncontactAPI instance for supervisor session
 * phone                   : AudioCodesUA instance for interpreter softphone
 * supervisorPhone         : AudioCodesUA instance for supervisor softphone
 * phoneConnectionConfig   : softphone reconnect, register, keep-alive settings
 *
 * -- Knockout observables (initialized in initializeObservablesState()) --
 * isPageLoading, isCallInfoVisible, isVideoVisible, isAudioVisible,
 * currentCallInfoStartTime, currentAgentStatus, currentAgentName,
 * elapsedAudioCallTime, elapsedInterpreterTime, sessionTitle,
 * supervisorSessionId, monitoredSessionId, etc.
 *
 * Computed observables and subscriptions are set up in initializeComputedObservables()
 * and in the constructor (e.g. mute toggles, session refresh).
 */
export class SessionViewModel {
  constructor(parent) {
    this.parent = parent;
    // Subscribers for media streams, used for custom mute controls
    this.clientSubscriber = null;
    this.interpreterSubscriber = null;
    this.logViewModel = new LogViewModel();
    this.loginViewModel = new LoginViewModel(this.handleSystemError);
    this.loginViewModel.isSupervisor(true);
    this.appViewModel = new AppViewModel();
    this.alertViewModel = new AlertViewModel();
    this.loginViewModel.setLogger(this.logViewModel);
    this.supervisorCheckViewModel = new SupervisorCheckViewModel();
    this.configLoaded = false;

    this.initializeSessionState();
    this.initializeObservablesState();
    // Subscribe to mute toggles to apply custom audio subscription when mute state changes
    this.isInterpreterMutedByUser.subscribe((muted) => {
      if (this.interpreterSubscriber) {
        this.interpreterSubscriber.subscribeToAudio(!muted);
      }
    });
    this.isClientMutedByUser.subscribe((muted) => {
      if (this.clientSubscriber) {
        this.clientSubscriber.subscribeToAudio(!muted);
      }
    });
    // Local mute toggle handlers
    this.toggleInterpreterAudio = () => {
      this.isInterpreterMutedByUser(!this.isInterpreterMutedByUser());
    };
    this.toggleClientAudio = () => {
      this.isClientMutedByUser(!this.isClientMutedByUser());
    };
    this.initializeComputedObservables();
    // Debug subscriptions: log when session IDs change
    this.supervisorSessionId.subscribe((val) =>
      logger.debug("supervisorSessionId updated =>", val),
    );
    this.monitoredSessionId.subscribe((val) => logger.debug("monitoredSessionId updated =>", val));

    this.loadConfigOnce().catch((error) => {
      logger.error("Failed to load configuration:", error);
      this.handleSystemError(error);
    });

    this.agentLegManager = new AgentLegManager(document.getElementById("audio_remote"));
  }

  async loadConfigOnce() {
    if (!this.configLoaded) {
      await this.loginViewModel.handleLoadConfig();
      this.configLoaded = true;
    }
  }

  /**
   * Primary entry point: authenticate, validate, process URL, and init sessions.
   */
  async start() {
    logger.info("Starting service monitoring", {
      monitoredAgentId: sessionStorage.getItem("monitoredAgentId"),
    });

    try {
      // Authenticate to InContact
      const tempApi = await this.handleAuthentication();

      // Supervisor validation section
      await this.validateSupervisorAccess(tempApi);

      // Parameter processing section
      if (!this.processUrlParameters()) {
        return;
      }

      // Initialize this first to ensure we have an agent ID for the softphone
      await this.initializeSupervisorApi();

      try {
        await this.initializeSoftPhone();
      } catch (error) {
        this.handleStartError("Failed to start softphone");
        return;
      }

      // Start the supervisor session and optionally subscribe to the interpreter session
      await this.initializeSessionComponents();

      logger.info("Service monitoring started successfully");
    } catch (error) {
      this.handleStartError(error);
    } finally {
      this.isPageLoading(false);
    }
  }

  initializeSessionState() {
    this.monitoredAgentId = null;
    this.currentMonitorAgentName = null;
    this.monitorApi = null;
    this.supervisorApi = null;
    this.currentActiveContacts = [];
    this.currentUserName = null;
    this.currentUserId = null;
    this.currentUser = {};
  }

  initializeObservablesState() {
    const observableDefaults = {
      monitoredAgentId: "",
      isPageLoading: true,
      isCallInfoVisible: false,
      isVideoVisible: false,
      isPlaceholderVisible: true,
      isAudioVisible: false,
      isListeningToAudio: false,
      isUnsupportedMediaVisible: false,
      currentCallInfoLanguage: "Unknown",
      currentCallInfoId: "Unknown",
      currentCallInfoStartTime: "Unknown",
      currentCallInfoInterpretationStartTime: "Unknown",
      currentCallInfoMediaType: "Unknown",
      currentAgentStatus: "Unknown",
      currentAgentInfo: null,
      currentAgentName: this.currentMonitorAgentName,
      currentAudioCallCustomerId: "",
      currentAgentLegId: "",
      softphoneDialNumber: "",
      currentPlayingMessage: "",
      elapsedAudioCallTime: "--:--",
      elapsedInterpreterTime: "--:--",
      isInterpreterAudioDisabled: false,
      isInterpreterVideoDisabled: false,
      isClientAudioDisabled: false,
      isClientVideoDisabled: false,
      // Custom mute toggles for local controls
      isInterpreterMutedByUser: false,
      isClientMutedByUser: false,
      currentCallInfoClientId: "",
      // Add observables for session/user identification
      currentUserId: "",
      currentUserName: "",
      currentMonitorAgentName: "",
      sessionTitle: "",
      isLogVisible: false, // disabled by default; enable via ?showConsole=true
      // Message shown in placeholderTpl; updated by updateMonitoringMessaging()
      placeholderMessage: "Loading session...",
      isHeaderVisible: false, // disabled by default; enable via ?showHeader=true
      isAgentLegConnected: false, // becomes true once AgentLegActive event arrives
      supervisorSessionId: "",
      monitoredSessionId: "",
      // internal: last agent monitoring state to avoid re-triggering spinner repeatedly
      _lastMonitoringState: null,
    };

    Object.keys(observableDefaults).forEach((key) => {
      this[key] = ko.observable(observableDefaults[key]);
    });

    this.isSoftphoneConnecting = ko.observable(false);
  }

  initializeComputedObservables() {
    this.formattedCallInfoStartTime = ko.computed(() => {
      const parsedDate = Date.parse(this.currentCallInfoStartTime());
      return !isNaN(parsedDate)
        ? moment(parsedDate).format("YYYY-MM-DD HH:mm:ss")
        : this.currentCallInfoStartTime();
    });

    this.startTime = ko.observable(new Date());
    this.currentTime = ko.observable(new Date());
    this.runningTimeHHMM = ko.computed(() => {
      // Compute elapsed time since call start time: MM:SS if <1h, HH:MM:SS if >=1h
      const startTs = Date.parse(this.currentCallInfoStartTime());
      if (isNaN(startTs)) {
        return "--:--";
      }
      const diffMs = this.currentTime() - startTs;
      const totalSeconds = Math.max(Math.floor(diffMs / 1000), 0);
      const seconds = totalSeconds % 60;
      const minutes = Math.floor(totalSeconds / 60) % 60;
      const hours = Math.floor(totalSeconds / 3600);
      const ss = seconds.toString().padStart(2, "0");
      const mm = minutes.toString().padStart(2, "0");
      if (hours > 0) {
        const hh = hours.toString().padStart(2, "0");
        return `${hh}:${mm}:${ss}`;
      }
      return `${mm}:${ss}`;
    });

    this.sessionPageTitle = ko.observable(
      this.getSessionStorageItem("sessionPageTitle", "ServiceMonitor Monitoring Session"),
    );
  }

  startInterpreterEventPolling() {
    logger.info("Starting event polling for interpreter");

    this.pollInterpreterEvents();
  }

  pollInterpreterEvents() {
    this.monitorApi
      .nextEvents()
      .then((events) => {
        this.handleMonitoringEventSuccess(events);
      })
      .catch((error) => {
        this.handleMonitoringEventError(error);
      });
  }

  startSupervisorEventPolling() {
    logger.info("Starting event polling for supervisor");

    this.pollSupervisorEvents();
  }

  pollSupervisorEvents() {
    this.supervisorApi
      .nextEvents()
      .then((events) => {
        this.handleSupervisorEventSuccess(events);
      })
      .catch((error) => {
        this.handleSupervisorEventError(error);
      });
  }

  /**
   * Step 2: Start a new supervisor session or join if one is already in progress.
   * @returns {Promise<string|undefined>} the sessionId on success.
   */
  async createSupervisorSessionOrJoin() {
    logger.info("createSupervisorSessionOrJoin: starting session initialization");
    const maxAttempts = 3;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    let supervisorSessionId;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      logger.debug(`createSupervisorSessionOrJoin: startSession attempt ${attempt}`);
      try {
        const result = await this.supervisorApi.startSession("WebRTC", null);
        if (!result?.sessionId) {
          logger.info(
            "createSupervisorSessionOrJoin: detected existing supervisor session, joining instead",
            { attempt },
          );
          const joinResult = await this.supervisorApi.joinSession();
          supervisorSessionId = joinResult.sessionId;
          logger.info("createSupervisorSessionOrJoin: joinSession succeeded", {
            supervisorSessionId,
          });
        } else {
          supervisorSessionId = result.sessionId;
          logger.info("createSupervisorSessionOrJoin: startSession succeeded", {
            attempt,
            supervisorSessionId,
          });
        }
        this.logViewModel.addLog(
          (result?.sessionId ? "Supervisor session initialized: " : "Supervisor session joined: ") +
            supervisorSessionId,
        );
        break;
      } catch (error) {
        if (attempt < maxAttempts) {
          logger.warn(
            `createSupervisorSessionOrJoin: startSession attempt ${attempt} failed, retrying...`,
            error,
          );
          await sleep(2000);
        } else {
          this.handleSupervisorEventError(error);
          this.logViewModel.addLog("Supervisor session failed to initialize.", "error");
        }
      }
    }
    return supervisorSessionId;
  }

  async initializeSupervisorApi() {
    // Ensure supervisorApi is initialized before starting or joining session
    this.supervisorApi = await this.loginViewModel.getSupervisorIncontactAPI();
    await this.supervisorApi.doDiscovery();

    const auth = JSON.parse(sessionStorage.getItem("auth"));
    if (!auth) {
      logger.error("Should be authenticated but failed to get auth details from session storage");
      return;
    }

    this.supervisorApi.agentId = auth.agentId;
  }

  /**
   * Orchestrates supervisor session initialization by attempting reload first,
   * then creating or joining a new session if no reload occurred.
   */
  async initializeSupervisorSession() {
    logger.info("initializeSupervisorSession: starting");

    const supervisorSessionId = await this.createSupervisorSessionOrJoin();
    logger.info("initializeSupervisorSession: finished session initialization", {
      supervisorSessionId,
    });
    if (supervisorSessionId) {
      // Ensure API instance reflects the new sessionId
      this.supervisorApi.sessionId = supervisorSessionId;
      this.supervisorSessionId(supervisorSessionId);
      this.startSupervisorEventPolling();
    }
  }

  async initializeMonitoringSession() {
    logger.debug("Initializing Interpreter Monitoring Session");

    this.monitorApi = await this.loginViewModel.getInterpreterIncontactAPI();
    // Set the interpreter agentId on the API (seeded earlier via URL)
    this.monitorApi.agentId = this.monitoredAgentId();
    // TODO: Why do we need to authenticate for the interpreter's API instance? Should be using the supervisor's credentials
    await this.monitorApi.doDiscovery();
    // Attempt to re-attach existing interpreter session on reload if supported
    const code = sessionStorage.getItem("incontact_auth_code");
    // Log interpreter's own agentId via InContactAPI instance
    logger.debug("initializeMonitoringSession: monitorApi.agentId = %s", this.monitorApi.agentId);

    // Use the observable's value for the monitored agent ID, and raw call ID
    this.targetAgentId = this.monitoredAgentId();

    const data = await this.monitorApi.getAgentInfoSync(this.monitorApi.agentId);
    this.currentAgentInfo(data);
    this.currentAgentName(`${data.agents[0].firstName} ${data.agents[0].lastName}`);

    try {
      // set the initial agent state
      const agentStates = await this.monitorApi.getAgentState(this.monitorApi.agentId);
      this.currentAgentStatus(agentStates.agentStates[0].agentStateName);
      // Notify parent of agent status (for status widget)
      this.sendMessage("agentStatus", {
        agentId: this.targetAgentId,
        status: this.currentAgentStatus(),
      });
    } catch (e) {
      logger.error("Error obtaining the initial agent state");
      return;
    }

    this.logViewModel.addLog("Joining session as Agent: " + this.targetAgentId);
    console.debug(
      "targetAgentId=",
      this.targetAgentId,
      "prevSessionId=",
      this.monitorApi.sessionId,
    );

    // we get a 409 if a session is already in progress. We always want to join an existing session here as the interpreter should already be logged in
    let monitoredSessionId;
    try {
      // TODO: after an interpreter logs out (and we've detected the logout) there's still several attempts to rejoin the (now invalid) session before it realizes it should stop
      const result = await this.monitorApi.joinSessionAsAgent(this.targetAgentId);
      monitoredSessionId = result.sessionId;
      console.debug("joinSessionAsAgent result=", result);
      // The monitorApi.sessionId setter now handles persistence
      this.loginViewModel.isSessionStarted(true);
      this.monitorSessionStarted = true;
    } catch (error) {
      this.monitorSessionStarted = false;
      this.handleMonitoringEventError(error);
    }

    // Only poll interpreter events once we have a valid session ID
    if (monitoredSessionId) {
      // Reflect interpreter session in UI header
      this.monitoredSessionId(monitoredSessionId);
      this.startInterpreterEventPolling();
    } else {
      logger.warn("Cannot initialize monitoring session without session ID");
    }
  }

  handleSystemError(error) {
    logger.trace("handleSystemError(%o)", error);
  }

  getSessionStorageItem(item, defVal) {
    const result = sessionStorage.getItem(item);
    return result === undefined || result === null ? defVal : result;
  }

  handleSupervisorEventSuccess(events) {
    setTimeout(this.pollSupervisorEvents.bind(this), 1);
    events.forEach((sessionEvent) => {
      if (sessionEvent) {
        const eventDescription = sessionEvent.description
          ? sessionEvent.description
          : sessionEvent.type || sessionEvent.Type;
        logger.debug(`Supervisor Event: ${eventDescription}`);

        const eventType = sessionEvent.Type || sessionEvent.type;
        if (eventType === "AgentLeg") {
          this.agentLegManager.processAgentLegEvent(sessionEvent);

          if (sessionEvent.status === "Disconnected") {
            // After we process all events re-check to see if we're supposed to be audio monitoring still and if so reconnect the agent leg
            setTimeout(() => {
              if (this.currentActiveContacts.length > 0) {
                logger.info(
                  "Agent leg was disconnected but there's still an active call, reconnect the agent leg",
                );
                this.startAudioMonitoring();
              }
            }, 1);
          }
        }
      }
    });
  }

  handleSupervisorEventError(error) {
    let errorDescription;
    try {
      errorDescription = JSON.parse(error.responseText).error_description;
    } catch (_e) {
      errorDescription = null;
    }
    // If the interpreter session has ended, re-establish it (join new session)
    if (errorDescription === "SessionEnded") {
      logger.warn("Supervisor session ended; re-establishing session");
      // Clear interpreter session data via IncontactAPI
      this.supervisorApi.clearSessionData();
      // Reinitialize monitoring session to join a fresh interpreter session
      this.initializeSupervisorSession().catch((err) =>
        logger.error("Failed to re-establish supervisor session", err),
      );
      return;
    }

    logger.error("handleSupervisorEventError: %o", error);
    setTimeout(this.pollSupervisorEvents.bind(this), 1000);
  }

  handleMonitoringEventSuccess(events) {
    let hadActiveAudioContacts = this.currentActiveContacts.length > 0;

    events.forEach((sessionEvent) => {
      if (sessionEvent) {
        const eventDescription = sessionEvent.description
          ? sessionEvent.description
          : sessionEvent.type || sessionEvent.Type;
        logger.debug(`Interpreter Event: ${eventDescription}`);
        try {
          const rawType = sessionEvent.type || sessionEvent.Type;
          this.processMonitoringEvent(rawType, sessionEvent);
        } catch (error) {
          logger.error("PAGE Error: %o %o", error, sessionEvent);
        }
      }
    });

    let hasActiveAudioContacts = this.currentActiveContacts.length > 0;

    if (!hadActiveAudioContacts && hasActiveAudioContacts) {
      logger.info("New audio call has started");

      // A new audio call has started, request monitoring
      // TODO: sometimes when this is used to connect the agent leg it will connect the agent leg, but not provide audio, then shortly after the agent leg will disconnect and it correctly recovers the agent leg. But this is a disruption to the SLS.
      this.startAudioMonitoring();
    }

    if (this.loginViewModel.isSessionStarted()) {
      setTimeout(this.pollInterpreterEvents.bind(this), 1);
    }
  }

  processMonitoringEvent(eventType, sessionEvent) {
    switch (eventType) {
      case "AgentSessionStart":
        this.handleStartSession(sessionEvent);
        break;
      case "AgentState":
        this.handleChangeState(sessionEvent);
        break;
      case "AgentSessionEnd":
        this.handleEndSession(sessionEvent);
        break;
      case "AgentError":
        this.handleAgentError(sessionEvent);
        break;
      case "CallContactEvent":
        this.handleCallContact(sessionEvent);
        break;
      case "WorkItemContactEvent":
        this.handleWorkItem(sessionEvent);
        break;
    }
  }

  handleMonitoringEventError(error) {
    logger.error("handleMonitoringEventError: %o", error);
    let msg = "Unknown page error getting events.";
    if (error) {
      let errorDescription;
      try {
        errorDescription = JSON.parse(error.responseText).error_description;
      } catch (_e) {
        errorDescription = null;
      }
      // If the interpreter session has ended, re-establish it (join new session)
      if (errorDescription === "SessionEnded") {
        logger.warn("Interpreter session ended; re-establishing session");
        // Clear interpreter session data via IncontactAPI
        this.monitorApi.clearSessionData();
        // Reinitialize monitoring session to join a fresh interpreter session
        this.initializeMonitoringSession().catch((err) =>
          logger.error("Failed to re-establish interpreter session", err),
        );
        return;
      }
      if (errorDescription === "InvalidAgentSession") {
        this.handleEndSession(null);
        return;
      } else {
        msg =
          error.statusCode === 0
            ? "Connection failed. You may be having internet problems."
            : `Error getting events: ${error.statusCode} - ${errorDescription}. Events have been halted.`;
      }
    }
    this.logViewModel.addLog(msg, "debug");
    if (this.loginViewModel.isSessionStarted()) {
      setTimeout(this.pollInterpreterEvents.bind(this), 1000);
    }
  }

  establishSessionConnection(apiKey, sessionId, token) {
    if (!this.tokBoxVideoSession || sessionId !== this.tokBoxVideoSession.sessionId) {
      try {
        this.tokBoxVideoSession = OT.initSession(apiKey, sessionId);
      } catch (e) {
        OT.reloadSession(apiKey, sessionId);
        logger.log("Reloading video session");
      }

      this.tokBoxVideoSession.on("streamCreated", this.handleStreamCreation.bind(this));
      this.tokBoxVideoSession.on("streamDestroyed", this.handleStreamDestruction.bind(this));
      this.tokBoxVideoSession.on("signal:message", this.handleMessageReceived.bind(this));
      this.tokBoxVideoSession.on(
        "streamPropertyChanged",
        this.handleStreamPropertyChange.bind(this),
      );

      this.isVideoVisible(true);
    }
    this.tokBoxVideoSession.connect(token, (error) => {
      logger.log("Connection error: " + error);
    });
  }

  handleStreamPropertyChange(event) {
    logger.debug("Stream Property Changed: %o", event);
    if (!event || !event.stream || !event.stream.connection || !event.stream.connection.data) {
      return;
    }

    const isClient = event.stream.connection.data.includes("endpoint=client");
    const isInterpreter = event.stream.connection.data.includes("endpoint=interpreter");

    // Track audio availability
    if (isClient && event.changedProperty === "hasAudio") {
      this.isClientAudioDisabled(!event.stream.hasAudio);
    } else if (isInterpreter && event.changedProperty === "hasAudio") {
      this.isInterpreterAudioDisabled(!event.stream.hasAudio);
    }
    // Track video availability
    if (isClient && event.changedProperty === "hasVideo") {
      this.isClientVideoDisabled(!event.stream.hasVideo);
    } else if (isInterpreter && event.changedProperty === "hasVideo") {
      this.isInterpreterVideoDisabled(!event.stream.hasVideo);
    }
  }

  handleStreamCreation(event) {
    if (!this.tokBoxVideoSession) {
      this.tokBoxVideoSession = event.target;
    }
    if (!event || !event.stream || !event.stream.connection || !event.stream.connection.data) {
      return;
    }

    const stream = event.stream;
    const connection = stream.connection;
    const data = connection.data.toLowerCase();
    logger.log("Stream data: " + data);
    let element;
    if (data.includes("endpoint=client")) {
      element = "client";
    } else if (data.includes("endpoint=interpreter")) {
      element = "interpreter";
    } else {
      // ignore any other (e.g. 3rd-party) streams
      logger.log(`Ignoring non-client/interpreter stream: ${data}`);
      return;
    }

    // Subscribe to stream, insert default UI but hide default controls, capture subscriber for custom mute
    const subscriber = this.tokBoxVideoSession.subscribe(
      stream,
      element,
      {
        fitMode: "contain",
        width: "100%",
        height: "100%",
        insertDefaultUI: true,
        insertMode: "append",
        showControls: false,
        style: {
          audioLevelDisplayMode: "off",
          nameDisplayMode: "off",
          buttonDisplayMode: "off",
        },
      },
      (error) => {
        if (!error) {
          this.tokBoxVideoSession.signal({
            type: "monitor",
            data: "",
            to: connection,
          });
        }
      },
    );
    // Store subscriber for custom mute/unmute
    if (element === "client") {
      this.clientSubscriber = subscriber;
    } else if (element === "interpreter") {
      this.interpreterSubscriber = subscriber;
    }

    // Initialize audio/video availability states
    if (element === "client") {
      this.isClientAudioDisabled(!event.stream.hasAudio);
      this.isClientVideoDisabled(!event.stream.hasVideo);
    } else if (element === "interpreter") {
      this.isInterpreterAudioDisabled(!event.stream.hasAudio);
      this.isInterpreterVideoDisabled(!event.stream.hasVideo);
    }
    const isClient = event.stream.connection.data.includes("endpoint=client");
    const isInterpreter = event.stream.connection.data.includes("endpoint=interpreter");

    if (isClient && event.changedProperty === "hasAudio") {
      this.isClientAudioDisabled(!event.stream.hasAudio);
    } else if (isInterpreter && event.changedProperty === "hasAudio") {
      this.isInterpreterAudioDisabled(!event.stream.hasAudio);
    }
  }

  handleStreamDestruction(event) {
    if (this.tokBoxVideoSession && event && event.stream) {
      const subscribers = this.tokBoxVideoSession.getSubscribersForStream(event.stream);
      if (subscribers && subscribers.length > 0) {
        subscribers.forEach((subscriber) => {
          this.tokBoxVideoSession.unsubscribe(subscriber);
          logger.log("Unsubscribed from stream");
        });
      }
    }
  }

  handleMessageReceived(event) {
    logger.debug("Message Received: %s", JSON.stringify(event.data, null, 2));
  }

  /**
   * Notify the container to navigate back (e.g. reload or redirect).
   * TODO: this can probably be removed, it is no longer used for anything
   */
  navigateToSupervisor() {
    this.sendMessage("navigate", { action: "reload" });
  }

  handleStartSession(event) {
    logger.log("Interpreter session started");
    // TODO: handle mid-session start events
  }

  handleEndSession(event) {
    logger.log("Interpreter session ended");
    // Restart interpreter status check
    this.checkInterpreterStatus().then();
    // TODO: why are we aggressively cleaning up when an interpreter logs out (a common occurrence) when we desire to remain connected?
    // this.loginViewModel.clear();
    // this.logViewModel.addLog("Agent session changed: Disconnected");
    this.alertViewModel.addWarning("Interpreter has ended their session.");
  }

  handleChangeState(event) {
    let newState = event.currentState;
    let newReason = event.currentOutReason;
    let combinedState = newReason ? `${newState} (${newReason})` : newState;

    logger.log(`Interpreter state changed: ${combinedState} ${event.isAcw ? "ACW State" : ""}`);

    // Update placeholder messaging when interpreter state changes
    this.updateMonitoringMessaging(newState, newReason, event.isAcw);

    this.currentAgentStatus(combinedState);

    // Notify parent of updated agent state
    this.sendMessage("agentStatus", {
      agentId: this.targetAgentId,
      status: this.currentAgentStatus(),
    });

    this.refreshCallInfo(newState);
    this.logViewModel.addLog("Agent state changed: " + newState);
  }

  refreshCallInfo(currentState) {
    const callInfo = {
      Available: {
        isCallInfoVisible: false,
        isVideoVisible: false,
        isAudioVisible: false,
        // Keep placeholder visible until a real call arrives
        isPlaceholderVisible: true,
        isUnsupportedMediaVisible: false,
        startTime: "Awaiting Call",
        mediaType: "Awaiting Call",
        callId: "Awaiting Call",
        language: "Awaiting Call",
      },
      Unavailable: {
        isCallInfoVisible: false,
        isVideoVisible: false,
        isAudioVisible: false,
        // Show placeholder to indicate unavailable status until interpreter becomes available
        isPlaceholderVisible: true,
        isUnsupportedMediaVisible: false,
        startTime: "Interpreter Unavailable",
        mediaType: "Interpreter Unavailable",
        callId: "Interpreter Unavailable",
        language: "Interpreter Unavailable",
      },
    };

    if (callInfo[currentState]) {
      const {
        isCallInfoVisible,
        isVideoVisible,
        isAudioVisible,
        isPlaceholderVisible,
        isUnsupportedMediaVisible,
        startTime,
        mediaType,
        callId,
        language,
      } = callInfo[currentState];
      this.isCallInfoVisible(isCallInfoVisible);
      this.isVideoVisible(isVideoVisible);
      this.isAudioVisible(isAudioVisible);
      this.isPlaceholderVisible(isPlaceholderVisible);
      this.isUnsupportedMediaVisible(isUnsupportedMediaVisible);
      this.currentCallInfoStartTime(startTime);
      this.currentCallInfoMediaType(mediaType);
      this.currentCallInfoId(callId);
      this.currentCallInfoLanguage(language);
    }
  }

  handleCallContact(event) {
    logger.info("Handling Call Contact: %s %o", event.status, event);
    logger.debug("CallContactEvent inbound flags", {
      rawIsInbound: event.rawIsInbound,
      isInbound: event.isInbound,
      contactId: event.contactId,
      status: event.status,
    });

    if (!["Active", "Joined", "Disconnected"].includes(event.status)) {
      return;
    }

    this.updateActiveContacts(event);
  }

  updateActiveContacts(event) {
    console.debug("updateActiveContacts called with: %o", event);

    if (["Active", "Joined"].includes(event.status)) {
      if (!this.currentActiveContacts.includes(event.contactId)) {
        this.currentActiveContacts.push(event.contactId);
      }
    } else if (event.status === "Disconnected") {
      this.currentActiveContacts = this.currentActiveContacts.filter(
        (item) => item !== event.contactId,
      );
      if (this.currentActiveContacts.length > 0) {
        return;
      }
    }

    if (!event.isInbound) {
      return;
    }

    if (event.status === "Active") {
      this.currentCallInfoStartTime(event.startTime);
      this.currentCallInfoInterpretationStartTime(event.startTime);
    }

    if (this.currentActiveContacts.length <= 1) {
      this.isCallInfoVisible(true);
      this.currentCallInfoId(event.contactId);
      this.currentCallInfoStartTime(event.startTime);
      this.updateScreenPopVariables(event.screenPopVariables);
      this.currentCallInfoMediaType("Audio");
    } else {
      return;
    }

    this.isPlaceholderVisible(false);
    if (["Active", "Joined"].includes(event.status)) {
      logger.debug("Resolved masterContactId", {
        referenceNumber: event.referenceNumber,
        contactId: event.contactId,
        screenPopVariables: event.screenPopVariables,
      });
      const payload = {
        masterContactId: event.referenceNumber ?? event.contactId,
        mediaType: "Audio",
        status: event.status,
      };
      this.sendMessage("callStatus", payload);
      this.isAudioVisible(true);
      this.isListeningToAudio(true);
    } else if (event.status === "Disconnected") {
      this.currentCallInfoStartTime(null);
      this.processAudioCallEnd(event);
      return;
    } else {
      this.isAudioVisible(false);
      this.isListeningToAudio(false);
    }

    this.isVideoVisible(false);
    this.isUnsupportedMediaVisible(false);
    this.currentAudioCallCustomerId("Customer: " + event.ani);
    this.logViewModel.addLog(
      "Incoming audio call -- " +
        "Ref. No.: " +
        this.currentCallInfoId() +
        ", Contact: " +
        event.contactId +
        ", Language: " +
        this.currentCallInfoLanguage(),
    );
  }

  updateScreenPopVariables(screenPopVariables) {
    if (!screenPopVariables) return;

    const entries = Array.isArray(screenPopVariables) ? screenPopVariables : [screenPopVariables];

    entries.forEach((entry) => {
      if (!entry) return;

      // Handle language (several naming variations)
      const langKey = Object.keys(entry).find((k) =>
        ["language", "splanguage", "spLanguage"].includes(k),
      );
      if (langKey && entry[langKey]) {
        this.currentCallInfoLanguage(decodeURIComponent(String(entry[langKey])).replace("+", " "));
      } else if (entry.name && /splanguage|language/i.test(entry.name) && entry.value) {
        this.currentCallInfoLanguage(decodeURIComponent(String(entry.value)).replace("+", " "));
      }

      // Handle masterContactId (case-insensitive and various shapes)
      const mciKey = Object.keys(entry).find((k) => k.toLowerCase() === "mastercontactid");
      if (mciKey && entry[mciKey]) {
        this.currentCallInfoId(String(entry[mciKey]));
      } else if (
        entry.name &&
        String(entry.name).toLowerCase() === "mastercontactid" &&
        entry.value
      ) {
        this.currentCallInfoId(String(entry.value));
      }
    });
  }

  processAudioCallEnd(event) {
    let didTransfer = event.disconnectCode === "TransferComplete";

    logger.info(`Clearing Call Status (transfer=${didTransfer})`);

    // Notify parent that call has ended (clear details)
    this.sendMessage("callStatus", {
      masterContactId: null,
      mediaType: "Audio",
      status: "Disconnected",
    });

    if (!event.finalState) {
      if (didTransfer) {
        this.alertViewModel.addWarning("Audio Call Transferred");
      } else {
        this.alertViewModel.addInfo("Audio Call Ended");
      }
    }

    this.isAudioVisible(false);
    this.isListeningToAudio(false);
  }

  async initializeSoftPhone(agentId) {
    logger.info("Initializing soft-phone");

    // Get soft-phone settings for supervisor
    const settings = await this.supervisorApi.getAgentSettings(this.supervisorApi.agentId);
    logger.debug("Retrieved Agent Settings: %o", settings);

    if (!settings) {
      this.alertViewModel.addWarning("Failed to retrieve softphone settings");
      return;
    }

    await this.agentLegManager.initialize(
      settings,
      agentId,
      this.softphoneHasInitialized.bind(this),
      this.softphoneCallConnected.bind(this),
    );
  }

  softphoneHasInitialized() {
    logger.info("Softphone has registered");
  }

  softphoneCallConnected() {
    logger.info("Softphone call has connected");

    this.isSoftphoneConnecting(false);
  }

  /**
   * Requests the interpreter's current call to be monitored by the supervisor
   * Unfortunately this API only applies to the current call so it must be called for every new audio call
   */
  startAudioMonitoring() {
    this.supervisorApi
      .monitorAgent(this.monitoredAgentId())
      .then((data) => {
        logger.info("Successfully requested audio monitoring");
      })
      .catch(async (error) => {
        logger.error(error, "Failed to request audio monitoring");
        // TODO: re-attempt monitoring?
      });
  }

  processVideoCallEnd(event) {
    let didTransfer = event.disconnectCode === "TransferComplete";

    logger.info(`Clearing Call Status (transfer=${didTransfer})`);

    this.logViewModel.addLog("Video call complete -- " + event.status);
    this.isVideoVisible(false);
    this.isPlaceholderVisible(false);
    this.currentCallInfoStartTime(event.status);
    this.currentCallInfoMediaType(event.status);
    this.currentCallInfoId(event.status);
    this.currentCallInfoLanguage(event.status);

    if (!event.finalState) {
      if (didTransfer) {
        this.alertViewModel.addInfo("Video Call Transferred");
      } else {
        this.alertViewModel.addInfo("Video Call Ended");
      }
    }

    // Notify parent that video call has ended
    this.sendMessage("callStatus", {
      masterContactId: null,
      mediaType: "Video",
      status: event.status,
    });
  }

  handleWorkItem(event) {
    logger.log("Handling Work Item: %s, %o", event.status, event);

    if (event.status === "Disconnected") {
      this.processVideoCallEnd(event);
      return;
    }

    if (!event.workItemPayload) {
      logger.debug("Work Item Payload is null");
      return;
    }

    this.isCallInfoVisible(true);
    this.isVideoVisible(true);
    this.isPlaceholderVisible(false);
    this.isAudioVisible(false);
    this.isUnsupportedMediaVisible(false);

    const workItemPayload = JSON.parse(event.workItemPayload);
    logger.log("Handling Work Item Payload: %o", workItemPayload);

    const platform = workItemPayload.platform;

    if (workItemPayload.platform && workItemPayload.platform !== "TOKBOX") {
      this.logViewModel.addLog("Unsupported Media: " + workItemPayload.platform);
      this.isCallInfoVisible(true);
      this.isVideoVisible(false);
      this.isPlaceholderVisible(false);
      this.isAudioVisible(false);
      this.isUnsupportedMediaVisible(true);
      this.currentCallInfoLanguage(workItemPayload.language);
      this.currentCallInfoId(event.contactId);
      this.currentCallInfoStartTime(event.startTimeUTC);
      this.currentCallInfoInterpretationStartTime(event.startTimeUTC);
      this.currentCallInfoMediaType("Video (SIP)");
      if (event.status === "Incoming") {
        this.alertViewModel.addError("Incoming video call: Unsupported");
      }
      return;
    }

    if (workItemPayload.tokBoxMonitorToken) {
      sessionStorage.setItem("tokBoxMonitorToken", workItemPayload.tokBoxMonitorToken);
    } else {
      sessionStorage.removeItem("tokBoxMonitorToken");
      this.alertViewModel.addError("Monitoring Token is missing, contact support.");
    }

    sessionStorage.setItem("tokBoxApiKey", workItemPayload.tokBoxApiKey);
    sessionStorage.setItem("tokBoxSessionId", workItemPayload.tokBoxSessionId);
    sessionStorage.setItem("callInfoLanguage", workItemPayload.language);
    sessionStorage.setItem("callInfoInfoMediaType", workItemPayload.mediaType);
    sessionStorage.setItem("callInfoInfoContactId", event.ContactID);

    this.isCallInfoVisible(true);

    const tokBoxApiKey = sessionStorage.getItem("tokBoxApiKey");
    const tokBoxMonitorToken = sessionStorage.getItem("tokBoxMonitorToken");
    const tokBoxSessionId = sessionStorage.getItem("tokBoxSessionId");

    if (event.status === "Incoming") {
      this.logViewModel.addLog("Incoming video call -- " + this.getWorkItemDescription(event));
      this.isVideoVisible(false);
      this.isPlaceholderVisible(true);
      this.currentCallInfoStartTime("Incoming Call");
      this.currentCallInfoInterpretationStartTime("Incoming Call");
      this.currentCallInfoMediaType("Incoming Call");
      this.currentCallInfoId("Incoming Call");
      this.currentCallInfoLanguage("Incoming Call");
    } else if (event.status === "Active") {
      this.logViewModel.addLog("Loading video call -- " + this.getWorkItemDescription(event));
      if (tokBoxMonitorToken) {
        this.establishSessionConnection(tokBoxApiKey, tokBoxSessionId, tokBoxMonitorToken);
      }
      this.isAudioVisible(false);
      this.isVideoVisible(true);
      this.isPlaceholderVisible(false);
      this.currentCallInfoLanguage(workItemPayload.language);
      this.currentCallInfoId(event.contactId);
      this.currentCallInfoStartTime(event.startTimeUTC);
      this.currentCallInfoInterpretationStartTime(event.startTimeUTC);
      this.currentCallInfoMediaType(workItemPayload.mediaType);
      this.currentCallInfoClientId(
        workItemPayload.routingInfo?.ani != null ? `${workItemPayload.routingInfo.ani}` : "",
      );

      const payload = {
        masterContactId: event.referenceNumber ?? event.contactId,
        mediaType: "Video",
        status: event.status,
      };
      logger.debug("Resolved masterContactId", {
        referenceNumber: event.referenceNumber,
        contactId: event.contactId,
        screenPopVariables: event.screenPopVariables,
      });
      this.sendMessage("callStatus", payload);
    } else {
      this.isVideoVisible(false);
      this.isPlaceholderVisible(false);
    }
  }

  handleAgentError(event) {
    // TODO: Determine if anything is needed here still
    // if (event.command === "LogoffAgent") {
    //   this.loginViewModel.setStatusMessage(
    //     "You cannot be logged off because you are handling one or more active contacts. Complete your contacts and the logout",
    //   );
    // } else if (event.command === "ReadyAgentLeg" && event.resultCode !== "Dropped") {
    //   this.loginViewModel.setStatusMessage("Could not dial the Agent Leg.");
    // } else {
    //   this.logViewModel.addLog("Agent Error! Command: " + event.command);
    // }
  }

  clearSessionStorageBeforeRedirect() {
    const keysToRemove = [
      // Session parameters seeded from URL
      "monitoredAgentId",
      "monitorCallId",
      // Supervisor session persistence
      "supervisor.sessionId",
      "supervisor.reloadSessionId",
      // Interpreter session persistence
      "interpreter.sessionId",
      "interpreter.reloadSessionId",
      // Softphone/DNIS keys
      "softPhoneDNIS",
      // TokBox session data
      "tokBoxApiKey",
      "tokBoxMonitorToken",
      "tokBoxSessionId",
      "callInfoLanguage",
      "callInfoInfoMediaType",
      "callInfoInfoContactId",
      // Login context
      "service-monitor.username",
      "returnPage",
    ];
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  async handleAuthentication() {
    const code = sessionStorage.getItem("incontact_auth_code");
    const tempApi = await this.loginViewModel.getSupervisorIncontactAPI();

    const loginStateLoggedIn = {
      loggedIn: true,
    };

    const loginStateNotLoggedIn = {
      loggedIn: false,
    };

    if (code) {
      logger.info("Authentication code found, authenticating");
      sessionStorage.removeItem("incontact_auth_code");
      this.logViewModel.addLog("Authenticating with Code: " + code);

      try {
        await tempApi.authenticateWithCode(code);
        this.sendDataToParent(loginStateLoggedIn);
        logger.info("Successfully authenticated with code");
      } catch (error) {
        logger.error("Failed to authenticate with code", error);
        this.sendDataToParent(loginStateNotLoggedIn);
        throw new Error("Authentication failed: " + error.message);
      }
    }

    const auth = JSON.parse(sessionStorage.getItem("auth"));
    if (!auth) {
      this.logViewModel.addLog("Authorization Unknown - redirecting to Auth Provider");
      this.sendDataToParent(loginStateNotLoggedIn);
      await this.loginViewModel.redirectToAuthProvider();
      throw new Error("No authentication found");
    }

    if (auth.apiExpiration < Date.now()) {
      this.logViewModel.addLog("Authorization Expired - redirecting to Auth Provider");
      this.sendDataToParent(loginStateNotLoggedIn);
      await this.loginViewModel.redirectToAuthProvider();
      throw new Error("Authentication expired");
    }

    this.sendDataToParent(loginStateLoggedIn);

    return tempApi;
  }

  processUrlParameters() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    const interpreterAgentId = params.get("agentId");
    const contactId = params.get("contactId");
    const showConsole = params.get("showConsole");
    const showHeader = params.get("showHeader");

    if (interpreterAgentId) {
      // Seed monitoredAgentId from URL parameters
      this.monitoredAgentId(interpreterAgentId);
      logger.info("Monitored Agent ID set", { monitoredAgentId: interpreterAgentId });
      this.logViewModel.addLog("Monitored Agent ID: " + interpreterAgentId);
    }

    if (contactId) {
      sessionStorage.setItem("monitorCallId", contactId);
      this.logViewModel.addLog("Monitor Contact ID (call): " + contactId);
    }

    if (showConsole && showConsole.toLowerCase() === "true") {
      this.isLogVisible(true);
    }

    if (showHeader && showHeader.toLowerCase() === "true") {
      // logger.debug("showHeader param detected, header visible");
      this.isHeaderVisible(true);
    }

    if (!interpreterAgentId && !contactId) {
      this.alertViewModel.addError(
        "Please supply a page parameter for [agentId] or [contactId] to begin monitoring.",
      );
      return false;
    } else {
      return true;
    }
  }

  async validateSupervisorAccess(tempApi) {
    const auth = JSON.parse(sessionStorage.getItem("auth"));
    if (!auth) {
      this.logViewModel.addLog("Authorization data not found in session storage.");
      throw new Error("Authorization data not found");
    }

    // Validate supervisor profile

    try {
      await this.supervisorCheckViewModel.checkSupervisorProfile(tempApi, auth.agentId);

      const currentUserAgentInfo = await tempApi.getAgentInfoSync(auth.agentId);
      this.currentUser = currentUserAgentInfo.agents[0];
      this.currentUserId(this.currentUser.agentId);
      this.currentUserName(this.currentUser.userName);
    } catch (error) {
      this.logViewModel.addLog("Failed to validate supervisor profile: " + error.message);
      throw new Error("Supervisor profile validation failed: " + error.message);
    }

    const isSupervisor = sessionStorage.getItem("isSupervisor") === "true";
    if (!isSupervisor) {
      this.logViewModel.addLog(
        "Insufficient Permissions. Validate that your InContact Profile is allowed for monitoring.",
      );
      throw new Error("Insufficient supervisor permissions");
    }

    // Initialize supervisor session
    this.logViewModel.addLog("Refreshing session");
  }

  /**
   * Step 3: Check interpreter status and dispatch appropriate actions or placeholder messages.
   */
  async checkInterpreterStatus() {
    const monitoredAgentId = this.monitoredAgentId();
    if (!monitoredAgentId) {
      this.logViewModel.addLog("Agent ID is missing.");
      throw new Error("Agent ID is missing");
    }
    logger.info("checkInterpreterStatus: checking monitored interpreter status", {
      monitoredAgentId,
    });
    this.monitorApi = await this.loginViewModel.getInterpreterIncontactAPI();
    await this.monitorApi.doDiscovery();
    try {
      const { agentStates } = await this.monitorApi.getAgentState(monitoredAgentId);
      const interpState = agentStates[0].agentStateName;
      this.currentAgentStatus(interpState);
      if (interpState === "LoggedOut") {
        this.placeholderMessage(
          "Interpreter is logged out; waiting for interpreter to log back in...",
        );
        this.isPageLoading(false);
        logger.debug("Starting interpreter status polling");
        this.interpreterStatusPollId = setInterval(async () => {
          try {
            logger.debug("Updating interpreter status");
            const r = await this.monitorApi.getAgentState(monitoredAgentId);
            const s = r.agentStates[0].agentStateName;
            if (s === "LoggedOut") return;
            logger.info("Interpreter has logged back in, start or resume monitoring");
            clearInterval(this.interpreterStatusPollId);
            this.currentAgentStatus(s);
            this.updateMonitoringMessaging(s);
            this.initializeMonitoringSession();
          } catch (e) {
            logger.error("checkInterpreterStatus: error polling interpreter status", e);
          }
        }, 10000);
        return;
      }
      await this.updateMonitoringMessaging(interpState);
    } catch (err) {
      logger.error("checkInterpreterStatus: failed to retrieve interpreter status", err);
    }
  }

  /**
   * Step 4: Update monitoring messaging and UI based on the interpreter state.
   */
  updateMonitoringMessaging(state, reason = null, isAcw = false) {
    console.debug("New monitoring state: " + state);
    if (state === "Unavailable" && !isAcw) {
      this.placeholderMessage("Interpreter is away...");
    } else if (state === "Available" || (state === "Unavailable" && isAcw)) {
      this.placeholderMessage("Waiting for next call...");
    } else if (
      (state === "InboundContact" || state === "OutboundContact") &&
      this._lastMonitoringState !== state
    ) {
      this.isPlaceholderVisible(false);
      this.isAudioVisible(true);
    }
    this._lastMonitoringState = state;
  }

  async initializeSessionComponents() {
    sessionStorage["isSessionJoined"] = "false";
    this.setupTimersAndListeners();
    logger.info("Starting session initialization");
    await this.initializeSupervisorSession();
    await this.checkInterpreterStatus();
    // If an interpreter is signed in (any state except 'LoggedOut'), start a monitoring session
    if (this.currentAgentStatus() !== "LoggedOut") {
      await this.initializeMonitoringSession();
    }

    logger.debug("Session Components initialized successfully", {
      supervisorAgentId: this.supervisorApi.agentId,
      supervisorSessionId: this.supervisorApi.sessionId,
      monitoredAgentId: this.monitorApi.agentId,
      monitoredSessionId: this.monitorApi.sessionId,
    });
  }

  setupTimersAndListeners() {
    if (!this.timerInitialized) {
      setInterval(this.refreshTimer.bind(this), 1000);
      window.addEventListener("beforeunload", this.handleBeforeUnload.bind(this));
      // Save supervisor session ID before page unload to allow session re-attach on reload
      window.addEventListener("beforeunload", () => {
        // Persist supervisor session ID so initializeSupervisorSession can reload it
        const supId = sessionStorage.getItem("supervisor.sessionId");
        if (supId) {
          sessionStorage.setItem("supervisor.reloadSessionId", supId);
        }
        // Persist interpreter session (monitor agent session) for reload
        const tgtId = sessionStorage.getItem("interpreter.sessionId");
        if (tgtId) {
          sessionStorage.setItem("interpreter.reloadSessionId", tgtId);
        }
      });
      this.timerInitialized = true;
      logger.debug("Timers and event listeners initialized");
    }
  }

  handleStartError(error) {
    logger.error("Service monitoring failed to start", error);

    this.alertViewModel.addError(`Service monitoring failed to start: ${error}`);
  }

  refreshTimer() {
    this.currentTime(new Date());

    try {
      this.refreshAudioCallDuration();
    } catch (error) {
      logger.error("Error updating audio call running time: %o", error);
    }
  }

  refreshAudioCallDuration() {
    this.refreshElapsedTime(this.currentCallInfoStartTime(), this.elapsedAudioCallTime);
    this.refreshElapsedTime(
      this.currentCallInfoInterpretationStartTime(),
      this.elapsedInterpreterTime,
    );
  }

  refreshElapsedTime(startTime, elapsedTimeObservable) {
    try {
      const parsedDate = Date.parse(startTime);
      if (isNaN(parsedDate)) {
        elapsedTimeObservable("--:--");
        return;
      }
      const elapsedTime = formatDuration(moment() - moment(parsedDate));
      elapsedTimeObservable(elapsedTime);
    } catch (error) {
      logger.error("Error updating elapsed time: %o", error);
    }
  }

  handleBeforeUnload() {
    this.agentLegManager.shutdown();
  }

  getWorkItemDescription(workItem) {
    if (!workItem) {
      return "No work item";
    }
    if (!workItem.workItemPayload) {
      return "No work item payload";
    }
    const workItemPayload = JSON.parse(workItem.workItemPayload);
    return `Ref. No.: ${workItem.contactId}, Language: ${workItemPayload.language}`;
  }

  /**
   * Send a structured message to the parent frame.
   * @param {string} type  Message type identifier
   * @param {object} payload  Message payload data
   */
  sendMessage(type, payload) {
    // Debug: log outgoing messages with SPA prefix
    logger.debug("SPA → parent postMessage", { type, payload });
    // Use '*' carefully in production - ideally specify the exact parent origin
    window.parent.postMessage({ type, payload }, "*");
  }

  /**
   * Alias for raw postMessage; wraps payload in type 'raw'.
   * @deprecated Use sendMessage(type, payload) for structured messages.
   */
  sendDataToParent(data) {
    this.sendMessage("raw", data);
  }
}
